import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useReactiveVar } from '@apollo/client/react';
import {
  AuthError,
  AuthUser,
  fetchAuthSession,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  getCurrentUser as getCurrentUserAmplify,
  signOut
} from '@aws-amplify/auth';
import { useMsal } from '@azure/msal-react';
import { MobileDevice, Query } from 'graphql/generated/graphqlTypes';
import { GET_CURRENT_VISITOR } from 'graphql/queries';
import useMobileDeviceRegistrationSubscription from 'hooks/useMobileDeviceRegistrationSubscription';
import { useEffect, useRef, useState } from 'react';
import { authenticationFailAction, authenticationSuccessAction, signOutAction } from 'state/actions/auth';
import { setLoadingTokens, triggerFetchToken } from 'state/actions/graphqlToken';
import { authVar, graphqlTokenVar } from 'state/vars';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_CURRENT_VISITOR_TYPED: TypedDocumentNode<Pick<Query, 'getCurrentVisitor'>> = GET_CURRENT_VISITOR;

export const isUserUnauthenticatedError = (error: unknown): boolean =>
  error instanceof AuthError && error.name === 'UserUnAuthenticatedException';

const useAuth = () => {
  const { accounts, inProgress, instance } = useMsal();
  const { user, loading } = useReactiveVar(authVar);
  const { loading: tokenLoading, tokenType, isTriggerFetchToken } = useReactiveVar(graphqlTokenVar);
  const [loggedAccount] = accounts;
  const [cognitoUserData, setCognitoUserData] = useState<AuthUser>();
  const [cognitoUserAttributes, setCognitoUserAttributes] = useState<FetchUserAttributesOutput>();
  const [hasCognitoUserError, setHasCognitoUserError] = useState(false);
  // Only the latest getCognitoUserData() call may update state; older (or invalidated) calls are ignored.
  const cognitoRequestIdRef = useRef(0);
  const hasRederivedTokenRef = useRef(false);

  const [getCurrentVisitor, { error: userError, data, refetch: refetchCurrentVisitor, loading: loadingQuery }] =
    useLazyQuery(GET_CURRENT_VISITOR_TYPED);
  const hostID = data?.getCurrentVisitor.id;
  const hostMobileDevice = data?.getCurrentVisitor.mobileDevices;

  useEffect(() => {
    if (user === null && loggedAccount !== undefined && tokenType === 'ENTRA') {
      getCurrentVisitor();
    }
  }, [user, loggedAccount, tokenType]);

  useEffect(() => {
    if (userError?.message === 'Visitor not found') {
      handleLogout();
    }
  }, [userError]);

  useEffect(() => {
    if (user === null && cognitoUserData !== undefined && tokenType === 'COGNITO') {
      getCurrentVisitor();
    }
  }, [user, cognitoUserData, tokenType]);

  const getCognitoUserData = async () => {
    cognitoRequestIdRef.current += 1;
    const requestId = cognitoRequestIdRef.current;
    const isStale = () => requestId !== cognitoRequestIdRef.current;

    try {
      const userData = await getCurrentUserAmplify();
      if (isStale()) return;

      const userAttributes = await fetchUserAttributes();
      if (isStale()) return;

      setCognitoUserData(userData);
      setCognitoUserAttributes(userAttributes);
      setHasCognitoUserError(false);
      hasRederivedTokenRef.current = false;
    } catch (error) {
      // Logout invalidates in-flight requests (see handleLogout), so an unauthenticated error
      // reaching this point is unexpected: the session is gone while tokenType is still COGNITO.
      if (isStale()) return;

      // Re-derive the token so a missing session resolves to NONE and the user lands on login.
      // Only once per failure streak: if the session keeps looking valid to the token derivation,
      // fall through to the error state instead of looping.
      if (isUserUnauthenticatedError(error) && !hasRederivedTokenRef.current) {
        hasRederivedTokenRef.current = true;
        triggerFetchToken();
        return;
      }

      // Don't sign the user out: this may be a transient failure. Surface it so the user can retry.
      console.error('[useAuth] Failed to load Cognito user data', error);
      setHasCognitoUserError(true);
    }
  };

  const retryAuth = () => {
    setHasCognitoUserError(false);
    void getCognitoUserData();
  };

  useEffect(() => {
    // The failure streak ends once the token resolves to something other than COGNITO (e.g. NONE after logout).
    if (!tokenLoading && tokenType !== 'COGNITO') hasRederivedTokenRef.current = false;

    // Don't use stale token state while token re-derivation is in progress.
    if (!tokenLoading && tokenType === 'COGNITO' && !isTriggerFetchToken) {
      void getCognitoUserData();
    }

    // Runs on unmount and whenever the auth context changes (e.g. logout re-derives the token),
    // so a request still in flight can no longer update state.
    return () => {
      cognitoRequestIdRef.current += 1;
    };
  }, [tokenLoading, tokenType, isTriggerFetchToken]);
  useEffect(() => {
    if (!loadingQuery && data && !tokenLoading) {
      const host = data.getCurrentVisitor;
      if (tokenType === 'COGNITO') {
        if (cognitoUserData) {
          authenticationSuccessAction({
            id: host.id,
            email: host.email || undefined,
            name: cognitoUserAttributes?.name || cognitoUserData.signInDetails?.loginId || cognitoUserData.username,
            isAdmin: host.isAdmin,
            isEntraLess: host.identityProvider === 'ENTRA' ? false : true,
            mobileDevices: host.mobileDevices
          });
        }
      } else if (tokenType === 'ENTRA') {
        if (accounts.length > 0 && loggedAccount) {
          const { name } = loggedAccount;
          authenticationSuccessAction({
            id: host.id,
            email: host.email || undefined,
            name: name || '',
            isAdmin: host.isAdmin,
            isEntraLess: host.identityProvider === 'ENTRA' ? false : true,
            mobileDevices: host.mobileDevices
          });
        }
      }
    }
  }, [data, cognitoUserData, tokenLoading, tokenType]);

  useEffect(() => {
    // The onboarding flow ('TENANT') has no authenticated visitor — it authenticates with the URL
    // registration JWT, not Cognito/Entra. Resolve auth loading the same way as the unauthenticated
    // ('NONE') case, otherwise authVar.loading stays true forever and Router is stuck on <Loading />.
    const hasNoAuthenticatedUser = tokenType === 'NONE' || tokenType === 'TENANT';
    if (inProgress === 'none' && !tokenLoading && !isTriggerFetchToken && hasNoAuthenticatedUser && user === null) {
      authenticationFailAction();
    }
  }, [inProgress, tokenLoading, isTriggerFetchToken, tokenType, user]);

  useMobileDeviceRegistrationSubscription(hostID, refetchCurrentVisitor);

  const isMobileDeviceRegisteredWithAccess = (mobileDevices: MobileDevice[] = hostMobileDevice!) =>
    mobileDevices.some((mobileDevice) => mobileDevice.isRegistered && mobileDevice.role === 'ACCESS');

  const isWaitingForQrCodeValue = () => {
    if (
      hostMobileDevice?.some(
        (mobileDevices) =>
          mobileDevices.role === 'ACCESS' &&
          mobileDevices.isRegistered !== undefined &&
          mobileDevices.registrationToken !== undefined
      )
    )
      if (
        hostMobileDevice?.some(
          (mobileDevices) =>
            mobileDevices.role === 'ACCESS' && !mobileDevices.isRegistered && mobileDevices.registrationToken === null
        )
      )
        return true;
    return false;
  };

  const handleLogout = async () => {
    if (tokenType === 'ENTRA') {
      await instance.logout({ account: accounts[0], postLogoutRedirectUri: process.env.REACT_APP_SSO_REDIRECT_URI });
      signOutAction();
      setLoadingTokens();
    } else if (tokenType === 'COGNITO') {
      cognitoRequestIdRef.current += 1; // a pending user-data request must not resolve into a logged-out session
      await signOut();
      const authSession = await fetchAuthSession();
      if (!authSession.credentials) {
        signOutAction();
        // Re-run derivation: getCognitoToken finds no session, clears the credential, derives NONE.
        triggerFetchToken();
      }
    }
  };

  // The app stays in a loading state until every step of sign-in has fully settled —
  // this prevents briefly showing "logged out" or "logged in" before all the data is ready.
  //  - inProgress !== 'none'      → MSAL itself is mid-flow. Only relevant to the Microsoft SSO login
  //                                  path (or MSAL's redirect handling on app boot); Cognito email/password
  //                                  sign-in never touches this, since MSAL is never invoked for it.
  //  - loading                    → authVar's user is still being resolved
  const getIsLoading = () =>
    inProgress !== 'none' ||
    loading ||
    loadingQuery ||
    isWaitingForQrCodeValue() ||
    tokenLoading ||
    isTriggerFetchToken ||
    ((tokenType === 'COGNITO' || tokenType === 'ENTRA') && user === null);

  return {
    handleLogout,
    isUserAuthenticated: Boolean(user),
    isLoading: getIsLoading(),
    // A stale failure can't flip an already-loaded session into the error state.
    // Also scoped to COGNITO, so the error screen doesn't outlive the session (e.g. token re-derived to NONE).
    hasAuthError: hasCognitoUserError && cognitoUserData === undefined && tokenType === 'COGNITO',
    retryAuth,
    isMobileDeviceRegisteredWithAccess
  };
};

export default useAuth;
